import { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import TuitionFeeField from './TuitionFeeField';
import { ReimbursementFieldsConfig } from '../../../../../config/adminConfig';
import Table from '../../../../../components/common/Table/Table';
import CustomDatepicker from '../../../../../components/common/Forms/Datepicker';
import Notifications from '../../../../../components/common/Notifications';
import lockIcon from '../../../../../../public/assets/images/lockSolid.svg';
import pencilIcon from '../../../../../../public/assets/images/pencil-outline.svg';
import { fetchReimbursementCoursesList, fetchReimbursementCourseDetails, updateReimbursementDetails } from '../../../../../store/slices/Tuition/tuitionAdminSlice';
import Loader from '../../../../../components/common/Loader';
import { trackButtonClick, trackFormSave } from '../../../../../services/analytics';
import { safeGet } from '../../../../../utils/safeGet';
import UploadErrorBanner from '../../../../CitizenshipReimbursement/UploadDocument/ViewDetails/UploadErrorBanner';

const formatTimestamp = (t?: string | null): string => {
  if (!t) return "";
  const d = new Date(t), p = (n: number) => String(n).padStart(2, '0');
  return `${p(d.getMonth() + 1)}/${p(d.getDate())}/${d.getFullYear()} ${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}`;
};

const formatDateDashToSlash = (date?: string | null): string => {
  if (!date) return "";
  return date.split('-').join('/');
};

interface ReimbursmentTabProps {
  applicationData?: any;
}

const COURSE_COLUMNS = [
  { key: 'courseStartDate', label: 'Course start date' },
  { key: 'reimbTypeDesc', label: 'Course Type' },
  { key: 'courseName', label: 'Course name' },
  { key: 'courseStatus', label: 'Status' },
  { key: 'courseReadyForPayment', label: 'Ready for payment' },
  { key: 'lastUpdatedBy', label: 'Last updated by' },
  { key: 'lastUpdatedTime', label: 'Last updated time' },
  { key: 'action', label: 'Action' },
];

const TuitionReimbursmentTab = ({ applicationData }: ReimbursmentTabProps) => {
  const dispatch = useDispatch();
  const { loading, reimbursementCourses, courseDetails } = useSelector((state: any) => state.tuitionAdmin);
  const [expandedRowId, setExpandedRowId] = useState<string | null>(null);
  const [actuals, setActuals] = useState<Record<string, string>>({});
  const [reimbursableAmounts, setReimbursableAmounts] = useState<Record<string, string>>({});
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [showSuccessToast, setShowSuccessToast] = useState<boolean>(false);
  const [successMessage, setSuccessMessage] = useState<string>('');
  const [showErrorToast, setShowErrorToast] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [isInactivateCourse, setIsInactivateCourse] = useState<boolean | undefined>(undefined);
  const [isReadyForPayment, setIsReadyForPayment] = useState<boolean>(false); // Default to false (N)
  const [isInactivateCourseModified, setIsInactivateCourseModified] = useState<boolean>(false);
  const [isReadyForPaymentModified, setIsReadyForPaymentModified] = useState<boolean>(false);
  const [courseStartDateValue, setCourseStartDateValue] = useState<string>('');
  const [displayedCourseId, setDisplayedCourseId] = useState<string | null>(null);
  const [showValidError,setShowValidError]=useState<boolean>(false);
  const notificationRef = useRef<HTMLDivElement | null>(null);
  const [dynamicRemaining, setDynamicRemaining] = useState<number>(0);
  const reimbursableRef = useRef<Record<string, string>>({});
  const [lastModifiedField, setLastModifiedField] = useState<string | null>(null);
  // Preserve the original remainingBalance returned by the API when details load.
  // We'll use this as the hard cap for total reimbursables.
  const initialRemainingRef = useRef<number>(0);
  // Store initial travel fee actuals from backend to detect increases
  const initialTravelActualsRef = useRef<Record<string, string>>({});

  useEffect(() => {
    reimbursableRef.current = reimbursableAmounts;
  }, [reimbursableAmounts]);

  useEffect(() => {
    setExpandedRowId(null);
    setDisplayedCourseId(null);
    setShowSuccessToast(false);
    setSuccessMessage("");
    setShowErrorToast(false);
    setErrorMessage("");
    setShowValidError(false);
  }, [applicationData?.applicationId]);

  useEffect(() => {
    if (expandedRowId) {
      const initialActuals = ReimbursementFieldsConfig.reduce<Record<string, string>>(
        (acc, f) => {
          const data =  safeGet(courseDetails, applicationData);
          acc[f.id] = String(data?.[f.actualKey] ?? '');
          return acc;
        },
        {}
      );
      const initialReimbursable = ReimbursementFieldsConfig.reduce<Record<string, string>>(
        (acc, f) => {
          const data = safeGet(courseDetails, applicationData);
          const actualValue = f.reimbursableKey ? String(data?.[f.reimbursableKey] ?? '') : '';
          if (actualValue && actualValue.trim() !== '') {
            const numValue = Number.parseFloat(actualValue);
            if (!Number.isNaN(numValue)) {
              acc[f.id] = numValue.toFixed(2);
            }
          }
          return acc;
        },
        {}
      );
      
      // Store initial travel fee values to detect increases later
      const travelFieldIds = ['airfare', 'autoRental', 'hotel', 'parking', 'publicTransport', 'mileage'];
      const initialTravelActuals: Record<string, string> = {};
      travelFieldIds.forEach(id => {
        if (initialActuals[id]) {
          initialTravelActuals[id] = initialActuals[id];
        }
      });
      initialTravelActualsRef.current = initialTravelActuals;
      
      if (courseDetails?.courseStartDate) {
        const formattedDate = formatDateDashToSlash(courseDetails.courseStartDate);
        setCourseStartDateValue(formattedDate);
        initialActuals.courseStartDate = formattedDate;
      } else {
        setCourseStartDateValue('');
      }
      setActuals(initialActuals);
      setReimbursableAmounts(initialReimbursable);
      // Store the API remaining balance as the initial cap
      const initialData = safeGet(courseDetails, applicationData);
      initialRemainingRef.current = Number(initialData?.remainingBalance || 0);
      // Initialize the displayed dynamic remaining balance from the API on load
      setDynamicRemaining(initialRemainingRef.current);
    }
  }, [expandedRowId, courseDetails]);

  useEffect(() => {
    if (applicationData?.applicationId) {
      (dispatch as any)(
        fetchReimbursementCoursesList({
          applicationId: applicationData.applicationId,
          nuid: applicationData?.employeeNuId,
        })
      );
    }
  }, [applicationData?.applicationId, applicationData?.employeeNuId, dispatch]);

  useEffect(() => {
    if (courseDetails && expandedRowId) {
      let isInactive: boolean | undefined;
      if (courseDetails?.courseStatus === 'I') {
        isInactive = true;
      } else if (courseDetails?.courseStatus === 'A') {
        isInactive = false;
      } else {
        isInactive = undefined;
      }
      setIsInactivateCourse(isInactive);
      
      let isReadyForPay: boolean;
      if (courseDetails?.courseReadyForPayment === 'Y') {
        isReadyForPay = true;
      } else {
        isReadyForPay = false;
      }
      setIsReadyForPayment(isReadyForPay);
      
      if (courseDetails?.courseStartDate) {
        setCourseStartDateValue(courseDetails.courseStartDate);
      }
    }
  }, [courseDetails, expandedRowId]);

  useEffect(() => {
    if (courseDetails && expandedRowId) {
      setDisplayedCourseId(expandedRowId);
    }
  }, [courseDetails, expandedRowId]);

  useEffect(() => {
    if (showErrorToast || showSuccessToast) {
      setTimeout(() => {
        notificationRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
    }
  }, [showErrorToast, showSuccessToast]);

  const courseRows = reimbursementCourses && Array.isArray(reimbursementCourses) ? reimbursementCourses : [];

  const areAllActualFeesZero = () => {
    return ReimbursementFieldsConfig.every((cfg) => {
      if (cfg.readOnly || !cfg.actualKey) return true; 
      const userEditedValue = actuals[cfg.id];
      if (userEditedValue && userEditedValue.trim() !== '') {
        const numValue = Number.parseFloat(userEditedValue);
        return numValue === 0 || Number.isNaN(numValue);
      }
      const data = safeGet(courseDetails, applicationData);
      const actualValue = String(data?.[cfg.actualKey] ?? '0');
      const numValue = Number.parseFloat(actualValue);
      return numValue === 0 || Number.isNaN(numValue);
    });
  };

  const handleAddFeesClick = (row: any) => {
    console.log(`📊 Analytics: Add fees clicked - Course ${row.empCourseId}`)
    trackButtonClick('Add fees', 'tuition-admin-reimbursement', { courseId: row.empCourseId })
    if (expandedRowId === row.empCourseId) {
      setExpandedRowId(null);
      setActuals({});
      setReimbursableAmounts({});
      setIsEditing(false);
      setIsInactivateCourse(undefined);
      setIsReadyForPayment(false); // Reset to default (N)
      setIsInactivateCourseModified(false);
      setIsReadyForPaymentModified(false);
      setCourseStartDateValue('');
      setShowSuccessToast(false);
      setSuccessMessage('');
      setShowErrorToast(false);
      setErrorMessage('');
      setShowValidError(false);
    } else {
      setExpandedRowId(row.empCourseId);
      setActuals({});
      setReimbursableAmounts({});
      setIsEditing(false);
      setIsInactivateCourseModified(false);
      setIsReadyForPaymentModified(false);
      setCourseStartDateValue('');
      setShowSuccessToast(false);
      setSuccessMessage('');
      setShowErrorToast(false);
      setErrorMessage('');
      setShowValidError(false);
      (dispatch as any)(
        fetchReimbursementCourseDetails({
          applicationId: applicationData?.applicationId,
          empCourseId: row.empCourseId,
          nuid: applicationData?.employeeNuId,
        })
      );
    }
  };

  const data = safeGet(courseDetails, applicationData);
  const calculateTotal = (key: 'estimatedKey' | 'actualKey' | 'reimbursableKey') => {
    return ReimbursementFieldsConfig.reduce((sum, cfg) => {
      const fieldKey = cfg[key] as string;
      const value = Number.parseFloat(String(safeGet(data?.[fieldKey], 0)));
      return sum + (Number.isNaN(value) ? 0 : value);
    }, 0);
  };

  const calculateTotalReimbursable = () => {
    return ReimbursementFieldsConfig.reduce((sum, cfg) => {
      const value = Number.parseFloat(String(safeGet(reimbursableAmounts[cfg.id], '0')));
      return sum + (Number.isNaN(value) ? 0 : value);
    }, 0);
  };

  const totalEstimated = calculateTotal('estimatedKey');
  const calculateTotalActual = () => {
    return ReimbursementFieldsConfig.reduce((sum, cfg) => {
      const value = Number.parseFloat(String(actuals[cfg.id] ?? '0'));
      return sum + (Number.isNaN(value) ? 0 : value);
    }, 0);
  };
  const totalActual = calculateTotalActual();
  const totalReimbursable = calculateTotalReimbursable();

  const dynamicRemainingBalance = Number(dynamicRemaining || 0);

  const parseFeeAmount = (value:string,key: string) => {
    if(value==="actual"){
      return actuals[key] ? Number.parseFloat(actuals[key]) : 0;
    }
    else{
       return reimbursableAmounts[key] ? Number.parseFloat(reimbursableAmounts[key]) : 0;
    }
     
  };

  const addOptionalField = (payload: Record<string, any>, key: string, value: string | undefined) => {
    if (value && value.trim() !== '') {
      payload[key] = value;
    }
  };

  const buildPayload = () => {
       const payload: Record<string, any> = {
      actualTutionFeeDetail: parseFeeAmount('actual', 'tuition'),
      actualBooksFeeDetail: parseFeeAmount('actual', 'books'),
      actualLabFeeDetail: parseFeeAmount('actual', 'lab'),
      actualRegistFeeDetail: parseFeeAmount('actual', 'registration'),
      actualOtherFeeDetail: parseFeeAmount('actual', 'other'),
      actualAirfareBagFeeDetail: parseFeeAmount('actual', 'airfare'),
      actualAutoRentalFeeDetail: parseFeeAmount('actual', 'autoRental'),
      actualHotelFeeDetail: parseFeeAmount('actual', 'hotel'),
      actualParkTollFeeDetail: parseFeeAmount('actual', 'parking'),
      actualPublicTransportFeeDetail: parseFeeAmount('actual', 'publicTransport'),
      actualMilesDollarValueDetail: parseFeeAmount('actual', 'mileage'),
      approvedTutionFeeDetail: parseFeeAmount('reimburse', 'tuition'),
      approvedBooksFeeDetail: parseFeeAmount('reimburse', 'books'),
      approvedLabFeeDetail: parseFeeAmount('reimburse', 'lab'),
      approvedRegistFeeDetail: parseFeeAmount('reimburse', 'registration'),
      approvedOtherFeeDetail: parseFeeAmount('reimburse', 'other'),
      approvedAirfareBagFeeDetail: parseFeeAmount('reimburse', 'airfare'),
      approvedAutoRentalFeeDetail: parseFeeAmount('reimburse', 'autoRental'),
      approvedHotelFeeDetail: parseFeeAmount('reimburse', 'hotel'),
      approvedParkTollFeeDetail: parseFeeAmount('reimburse', 'parking'),
      approvedPublicTransportFeeDetail: parseFeeAmount('reimburse', 'publicTransport'),
      approvedMilesDollarValueDetail: parseFeeAmount('reimburse', 'mileage'),
      coursePaymentStatus: isReadyForPayment ? 'Y' : 'N',
      courseActiveStatus: isInactivateCourse ? 'I' : 'A',
    };
    const startDate = safeGet(courseStartDateValue, courseDetails?.courseStartDate);
    addOptionalField(payload, 'courseStartDate', startDate);

    if (isReadyForPaymentModified) {
      payload.coursePaymentStatus = isReadyForPayment ? 'Y' : 'N';
    }

    if (isInactivateCourseModified && isInactivateCourse !== undefined) {
      payload.courseActiveStatus = isInactivateCourse ? 'I' : 'A';
    }

    if (Object.hasOwn(actuals, 'courseComments')) {
      payload.courseComments = actuals['courseComments'] === '' ? null : String(actuals['courseComments']);
    } else {
      const apiComment = safeGet(courseDetails?.courseComments, '');
      addOptionalField(payload, 'courseComments', apiComment);
    }

    return payload;
  };

  const handleSuccessResponse = (result: any, courseId: string | null) => {
    const message = safeGet(result?.payload?.message, '');
    setSuccessMessage(message);
    setShowSuccessToast(true);
    
    if (courseId) {
      setTimeout(() => {
        Promise.all([
          (dispatch as any)(
            fetchReimbursementCourseDetails({
              applicationId: applicationData?.applicationId,
              empCourseId: courseId,
              nuid: applicationData?.employeeNuId,
            })
          ),
          (dispatch as any)(
            fetchReimbursementCoursesList({
              applicationId: applicationData?.applicationId,
              nuid: applicationData?.employeeNuId,
            })
          )
        ]).then(() => {
          setIsEditing(false);
          setSuccessMessage('');
          setShowSuccessToast(false);
          setShowErrorToast(false);
          setIsSaving(false); // Hide loader after refetch completes
        });
      }, 500);
    } else {
      setTimeout(() => {
        setShowSuccessToast(false);
        setIsEditing(false);
        setShowErrorToast(false);
        setIsSaving(false); // Hide loader
      }, 5000);
    }
  };

  const handleErrorResponse = (message: string = 'Failed to update reimbursement details') => {
    setErrorMessage(message);
    setShowErrorToast(true);
    setIsSaving(false); // Hide loader on error
    setTimeout(() => setShowErrorToast(false), 10000);
  };

  const handleCancelEdit = () => {
    if (!expandedRowId) return;
    
    // Refetch fresh data from backend
    (dispatch as any)(
      fetchReimbursementCourseDetails({
        applicationId: applicationData?.applicationId,
        empCourseId: expandedRowId,
        nuid: applicationData?.employeeNuId,
      })
    );
    
    // Reset editing state and form
    setIsEditing(false);
    setActuals({});
    setReimbursableAmounts({});
    setCourseStartDateValue('');
    setIsInactivateCourse(undefined);
    setIsReadyForPayment(false);
    setIsInactivateCourseModified(false);
    setIsReadyForPaymentModified(false);
    setShowErrorToast(false);
    setShowSuccessToast(false);
  };

  const saveHandler = async () => {
    console.log('📊 Analytics: Save reimbursement clicked')
    trackButtonClick('Save reimbursement', 'tuition-admin-reimbursement')
    if (!expandedRowId) return;

    setIsSaving(true);
    trackFormSave('tuition', undefined, { courseId: expandedRowId })
    
    const payload = buildPayload();
    const appId = applicationData?.applicationId;
    const nuid = applicationData?.employeeNuId;

    // Check if remaining travel balance is 0 and user is increasing any travel fees
    const remainingTravelBalance = data?.travelFeeLimit ?? 0;
    const travelFieldIds = ['airfare', 'autoRental', 'hotel', 'parking', 'publicTransport', 'mileage'];
    const isIncreasingTravelFees = travelFieldIds.some(id => {
      const currentValue = Number.parseFloat(actuals[id] ?? '0');
      const initialValue = Number.parseFloat(initialTravelActualsRef.current[id] ?? '0');
      return currentValue > initialValue;
    });

    if (remainingTravelBalance === 0 && isIncreasingTravelFees) {
      setErrorMessage('Travel balance is greater than travel fee limit. Please change.');
      setShowErrorToast(true);
      setIsSaving(false);
      setTimeout(() => setShowErrorToast(false), 10000);
      return;
    }

    try {
      const result = await (dispatch as any)(
        updateReimbursementDetails({
          applicationId: appId,
          empCourseId: expandedRowId,
          data: payload,
          nuid: nuid,
        })
      );
      if (result.type?.includes('rejected')) {
        const errorMsg = `No reimbursement details found to update for Application ID: ${appId}, Course ID: ${expandedRowId}`;
        handleErrorResponse(errorMsg);
        return;
      }

      if (result?.payload?.success === false) {
        // Refetch data when backend returns success === false
        setTimeout(() => {
          Promise.all([
            (dispatch as any)(
              fetchReimbursementCourseDetails({
                applicationId: appId,
                empCourseId: expandedRowId,
                nuid: nuid,
              })
            ),
            (dispatch as any)(
              fetchReimbursementCoursesList({
                applicationId: appId,
                nuid: nuid,
              })
            )
          ]).then(() => {
            handleErrorResponse(result?.payload?.message);
            setIsSaving(false);
          });
        }, 500);
        return;
      }

      handleSuccessResponse(result, expandedRowId);
    } catch (error) {
      const errorMsg = (error as any)?.message;
      handleErrorResponse(errorMsg);
    }
  };


  const normalizeInputValue = (raw: string): number => {
    const trimmed = String(safeGet(raw, '')).trim();
    let numValue = trimmed === '' ? 0 : Number.parseFloat(trimmed);
    return Number.isNaN(numValue) ? 0 : numValue;
  };

  const reallocateLastModifiedField = (
    remaining: number,
    updatedActuals: Record<string, string>,
    prevActuals: Record<string, string>,
    newReimbursable: Record<string, string>,
    editedFieldId: string
  ): number => {
    if (remaining <= 0 || !lastModifiedField || lastModifiedField === editedFieldId) {
      return remaining;
    }
    const lastId = lastModifiedField;
    const lastPrev = Number.parseFloat(newReimbursable[lastId] ?? '0') || 0;
    const lastActual = Number.parseFloat(updatedActuals[lastId] ?? prevActuals[lastId] ?? '0') || 0;
    if (lastActual <= lastPrev) return remaining;
    const need = lastActual - lastPrev;
    const apply = Math.min(need, remaining);
    if (apply > 0) {
      newReimbursable[lastId] = (lastPrev + apply).toFixed(2);
      return remaining - apply;
    }
    return remaining;
  };

  const distributeRemainingFunds = (
    remaining: number,
    updatedActuals: Record<string, string>,
    newReimbursable: Record<string, string>,
    editedFieldId: string
  ): number => {
    if (remaining <= 0) return remaining;
    for (const field of ReimbursementFieldsConfig) {
      const id = field.id;
      if (id === editedFieldId) continue;
      const actualVal = Number.parseFloat(updatedActuals[id] ?? '0') || 0;
      if (Number.isNaN(actualVal) || actualVal <= 0) continue;
      const current = Number.parseFloat(newReimbursable[id] ?? '0');
      const need = Math.max(0, actualVal - current);
      if (need <= 0) continue;
      const take = Math.min(need, remaining);
      newReimbursable[id] = (current + take).toFixed(2);
      remaining -= take;
      if (remaining <= 0) break;
    }
    return remaining;
  };

  const rowsWithActions = courseRows.map((row: any) => ({
    ...row,
    lastUpdatedTime: row.lastUpdatedTime===null?formatTimestamp(row.createdTime):formatTimestamp(row.lastUpdatedTime),
    lastUpdatedBy: row.lastUpdatedBy===null?row.createdBy:row.lastUpdatedBy,
    courseStatus: (
      <span className="bg-gray-100 border border-[#00000014] rounded px-1!  text-sm font-semibold inline-flex items-center justify-center">
        {row.courseStatus === 'A' ? "Active" : "Inactive"}
      </span>
    ),
    courseReadyForPayment:
      row.courseReadyForPayment === 'Y' ? 'Y' : 'N',
    action: (
      <button
        type="button"
        className={`button -primary ${expandedRowId === row.empCourseId ? '-light' : ''}`}
        onClick={() => handleAddFeesClick(row)}
        disabled={loading}
      >
        {loading ? 'Loading...' : 'Add fees'}
      </button>
    ),
  }));

  return (
    <div>
      {
        showValidError && (
          <div ref={el => {
            if (el) {
              el.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
          }}>
            <UploadErrorBanner message='The value entered is invalid' header='Error'/>
          </div>
        )
      }
      {isSaving && <Loader />}
      <p className="p1 -medium mb-4">Reimbursement</p>
      <p className='p3 -book mt-6!'>Document associated with this application</p>
      {reimbursementCourses && reimbursementCourses.length > 0 ? (
        <Table
          columns={COURSE_COLUMNS}
          rows={rowsWithActions}
        />
      ) : (
        <div className="p-4  rounded text-center">
          <p className="p3 -book">No courses found for given applicationId</p>
        </div>
      )}

      {displayedCourseId && expandedRowId === displayedCourseId && (
        <>
        <div className="flex items-center gap-4 mt-8! mb-4!">
          <button 
            type="button" 
            className="button -secondary flex items-center gap-2"
            onClick={() => setIsEditing(!isEditing)}
            disabled={isEditing}
          >
            <img src={pencilIcon} alt="edit" className={`w-6 h-6 ${isEditing ? 'opacity-50' : ''}`} /> {isEditing ? 'Editing' : 'Edit Fields'}
          </button>
        </div>

        {showSuccessToast && (
          <div ref={notificationRef}>
            <Notifications
              messageInfo={successMessage}
              status={true}
            />
          </div>
        )}

        {showErrorToast && (
          <div ref={notificationRef}>
            <Notifications
              messageInfo={safeGet(errorMessage, "Failed to update reimbursement details.")}
              status={false}
            />
          </div>
        )}

        <div className="flex flex-col gap-4 mt-12">
        {ReimbursementFieldsConfig.map((cfg) => {
          const data = safeGet(courseDetails, applicationData);
          return cfg.readOnly ? (
            <div key={cfg.id} className="form-group">
              <label className="p4 -medium">{cfg.helperText}</label>
              <div className="textarea-wrapper">
                <textarea
                  className="text-area_word-count text-[#0D1C3D]! text-area_global -book w-full"
                  value={actuals[cfg.id]}
                  onChange={(e) => {
                    setActuals((prev) => ({ ...prev, [cfg.id]: e.target.value }));
                  }}
                  maxLength={1000}
                  rows={5}
                  disabled={isInactivateCourse === true}
                />
                <div className="character-count">
                  <span className="count-area text-input-helper">
                    {1000 - safeGet(actuals[cfg.id]?.length, 0)} of 1000 characters left
                  </span>
                </div>
              </div>
            </div>
          ) : (
            <TuitionFeeField
              key={cfg.id}
              title={cfg.title}
              setShowValidError={setShowValidError}
              estimated={data?.[cfg.estimatedKey] ?? ''}
              actual={actuals[cfg.id]}
              reimbursable={safeGet(reimbursableAmounts[cfg.id], '')}
              onActualChange={(v) =>
                setActuals((prev) => ({ ...prev, [cfg.id]: v }))
              }
              onActualBlur={(v) => {
                const numValue = normalizeInputValue(v);
                const formattedValue = numValue.toFixed(2);
                setActuals((prevActuals) => {
                  const updatedActuals = { ...prevActuals, [cfg.id]: formattedValue };
                  const editedFieldId = cfg.id;
                  const prevReimb = Number.parseFloat(reimbursableRef.current[editedFieldId] ?? '0') || 0;
                  const currentDynamic = Number(dynamicRemaining || 0);
                  const availableForField = prevReimb + currentDynamic;
                  const enteredActual = Number.parseFloat(updatedActuals[editedFieldId] ?? '0') || 0;
                  const newReimb = Math.min(enteredActual, Math.max(0, availableForField));
                  const newReimbursable: Record<string, string> = { ...reimbursableRef.current };
                  newReimbursable[editedFieldId] = newReimb.toFixed(2);
                  let remaining = Math.max(0, currentDynamic - (newReimb - prevReimb));
                  remaining = reallocateLastModifiedField(remaining, updatedActuals, prevActuals, newReimbursable, editedFieldId);
                  remaining = distributeRemainingFunds(remaining, updatedActuals,newReimbursable, editedFieldId);
                  setReimbursableAmounts(newReimbursable);
                  setDynamicRemaining(remaining);
                  setLastModifiedField(editedFieldId);
                  return updatedActuals;
                });
              }}
              hideReimbursable={cfg.hideReimbursable}
              disabled={!isEditing || isInactivateCourse === true}
            />
          );
        })}
        </div>

        <div className="bg-[#E6F4FA] mt-9! p-5! w-full!">
          <div className="flex gap-8">
            <div className="flex-1">
              <div className="flex items-center gap-4 w-full min-w-0">
                <div className="shrink-0 min-w-[200px] whitespace-nowrap">
                  <div className="label-ds2 font-semibold">Total</div>
                </div>
                <div className="flex flex-1 items-start gap-3 min-w-0 flex-col">
                  <div className="w-full flex items-center gap-3">
                    <div className="ds-form flex-1 min-w-0" data-ds-theme="vessel" data-ds-variant="input">
                      <label htmlFor="estTotalAmount" className="mb-1!">Estimated total amount</label>
                      <div className="input-with-icon bg-gray-100" >
                        <input 
                          id="estTotalAmount"
                          type="text" 
                          className="w-full block ds-form__control" 
                          value={`$${totalEstimated.toFixed(2)}`}
                          disabled
                        />
                        <button type="button"  className="date-input-icon">
                          <img src={lockIcon} alt="locked" /></button>
                      </div>
                    </div>
                    <div className="ds-form flex-1 min-w-0" data-ds-theme="vessel" data-ds-variant="input">
                      <label htmlFor="actTotalAmount" className="mb-1!">Actual total amount</label>
                      <div className="input-with-icon bg-gray-100" >
                        <input 
                          id="actTotalAmount"
                          type="text" 
                          className="w-full block ds-form__control" 
                          value={`$${totalActual.toFixed(2)}`}
                          disabled
                        />
                        <button type="button"  className="date-input-icon">
                          <img src={lockIcon} alt="locked" />
                        </button>
                      </div>
                    </div>
                    <div className="ds-form flex-1 min-w-0" data-ds-theme="vessel" data-ds-variant="input">
                      <label htmlFor="reimbTotalAmount" className="mb-1!">Reimbursement total amount</label>
                      <div className="input-with-icon bg-gray-100" >
                        <input 
                          id="reimbTotalAmount"
                          type="text" 
                          className="w-full block ds-form__control" 
                          value={`$${totalReimbursable.toFixed(2)}`}
                          disabled
                        />
                        <button type="button"  className="date-input-icon">
                          <img src={lockIcon} alt="locked" />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="flex justify-end mt-6!">
            <div className="flex flex-col gap-4 max-w-[400px]">
              <div className="ds-form" data-ds-theme="vessel" data-ds-variant="input">
                <label htmlFor="remainingBalance" className="p4 -medium mb-1!">Remaining reimbursement balance</label>
                <div className="input-with-icon bg-gray-100" >
                  <input
                    id="remainingBalance"
                    type="text"
                    className="ds-form__control ds-form__-field bg-gray-100 text-gray-600 pr-12"
                    disabled
                    value={`$${dynamicRemainingBalance.toFixed(2)}`}
                  />
                  <button type="button"  className="date-input-icon">
                    <img src={lockIcon} alt="locked" />
                  </button>
                </div>
              </div>
              <div className="ds-form" data-ds-theme="vessel" data-ds-variant="input">
                <CustomDatepicker
                  name="courseStartDate"
                  label="Course start date"
                  value={courseStartDateValue}
                  onChange={(_name, value) => {
                    setCourseStartDateValue(value);
                    setActuals(prev => ({ ...prev, courseStartDate: value }));
                  }}
                  helperText="MM/DD/YYYY"
                  disabled={isInactivateCourse === true}
                />
              </div>
              <div className="mt-2 flex flex-col gap-2 ml-1">
                <label className="flex items-start cursor-pointer">
                  <input 
                    type="checkbox" 
                    className="cursor-pointer scale-75"
                    checked={isInactivateCourse === true}
                    onChange={(e) => {
                      setIsInactivateCourse(e.target.checked);
                      setIsInactivateCourseModified(true);
                    }}
                    disabled={!isEditing || loading || !areAllActualFeesZero()}
                  />
                  <span className="p3 -book leading-tight ml-8! mt-2.5!">Inactivate course</span>
                </label>
                <label className="flex items-start cursor-pointer">
                  <input 
                    type="checkbox" 
                    className="cursor-pointer scale-75"
                    checked={isReadyForPayment === true}
                    onChange={(e) => {
                      setIsReadyForPayment(e.target.checked);
                      setIsReadyForPaymentModified(true);
                    }}
                    disabled={!isEditing || loading || isInactivateCourse === true}
                  />
                  <span className="p3 -book leading-tight ml-8! mt-2.5!">Ready for payment</span>
                </label>
              </div>
            </div>
          </div>

          <div className="mt-6">
            <div className="flex items-center gap-4 w-full min-w-0">
              <div className="shrink-0 min-w-[200px] whitespace-nowrap">
                <div className="label-ds2 font-semibold">Total Travel</div>
              </div>
              <div className="flex flex-1 items-start gap-3 min-w-0 flex-col">
                <div className="w-full flex items-center gap-3">
                  <div className="ds-form flex-1 min-w-0" data-ds-theme="vessel" data-ds-variant="input">
                    <label htmlFor="totalTravelCurrent" className="mb-1!">Total travel (Current year)</label>
                    <div className="input-with-icon bg-gray-100" >
                      <input 
                        id="totalTravelCurrent"
                        type="text" 
                        className="w-full block ds-form__control" 
                        disabled
                        value={`$${data?.totalTravelFees?.toFixed(2) ?? '0.00'}`}
                      />
                      <button type="button"  className="date-input-icon">
                        <img src={lockIcon} alt="locked" />
                      </button>
                    </div>
                  </div>
                  <div className="ds-form flex-1 min-w-0" data-ds-theme="vessel" data-ds-variant="input">
                    <label htmlFor="remainingTravelBalance" className="mb-1!">Remaining travel balance</label>
                    <div className="input-with-icon bg-gray-100" >
                      <input 
                        id="remainingTravelBalance"
                        value={
                          data?.travelFeeLimit === -1
                            ? "Not applicable"
                            : `$${(data?.travelFeeLimit ?? 0).toFixed(2)}`
                        }
                        type="text" 
                        className="w-full block ds-form__control" 
                        placeholder="i.e. $725"
                        disabled
                      />
                      <button type="button"  className="date-input-icon">
                        <img src={lockIcon} alt="locked" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

              <div className="form-group mt-6! flex items-start">
            <div className="shrink-0 min-w-[200px] whitespace-nowrap"></div>
            <div className="flex-1">
              <label htmlFor="courseComments" className="p4 -medium">Course comments</label>
              <div className="textarea-wrapper">
                {(() => {
                  const apiComment = safeGet(data?.courseComments, '');
                  const editedComment = actuals['courseComments'];
                  // Use editedComment when present in state, even if it's an empty string
                  const commentValue = editedComment !== undefined && editedComment !== null ? String(editedComment) : apiComment;
                  return (
                    <>
                      <textarea
                        id="courseComments"
                        className="text-area_word-count text-[#0D1C3D]! text-area_global -book w-full bg-white"
                        value={commentValue}
                        onChange={(e) => setActuals((prev) => ({ ...prev, courseComments: e.target.value }))}
                        maxLength={4000}
                        rows={5}
                        disabled={!isEditing}
                      />
                      <div className="character-count">
                        <span className="count-area text-input-helper">
                          {4000 - commentValue.length} of 4000 characters left
                        </span>
                      </div>
                    </>
                  );
                })()}
              </div>
            </div>
          </div>          <div className="mt-6 flex gap-3">
            {isEditing && (
              <>
                <button 
                  type="button" 
                  className="button -secondary"
                  onClick={handleCancelEdit}
                >
                  Cancel
                </button>
                <button 
                  type="button" 
                  className="button -primary"
                  disabled={isSaving}
                  onClick={saveHandler}
                >
                  {isSaving ? 'Saving...' : 'Save'}
                </button>
              </>
            )}
          </div>
        </div>
        </>
      )}
    </div>
  );
};

export default TuitionReimbursmentTab;


